import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { whereAlpha2 } from 'iso-3166-1';
import { Country } from 'iso-3166-1/dist/iso-3166';
import { AlertService, AuthenticationService } from 'src/app/_services';
import { LocationSuggestion, LocationsService } from 'src/app/_services/locations.service';
import { PersistedProfile, Profile, ProfileService } from 'src/app/_services/profiles.service';

@Component({
  selector: 'profile-editor',
  templateUrl: './profile-editor.component.html',
  styleUrls: ['./profile-editor.component.scss']
})
export class ProfileEditorComponent implements OnInit {

  profile: Profile | undefined;

  profileForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    country: new FormControl<Country | null>(null, [Validators.required]),
    location: new FormControl<LocationSuggestion | null>(null, [Validators.required]),
  });
  
  constructor(
    private profileService: ProfileService,
    private alertService: AlertService,
    private locationsService: LocationsService,
    private authenticationService: AuthenticationService
  ) {
    
  }
  
  ngOnInit(): void {
    
    this.initForm();
    
    this.profileForm.controls["country"].valueChanges.subscribe(value => {
      
      if (!value) {
        console.log("Disable locations selection until country selected");
        this.profileForm.controls["location"].disable();
        return;
      }
       
      this.profileForm.controls["location"].enable();
      
      if (value.alpha3 != this.profile!.location.countryCode){
        this.resetLocationOptions();
      }
    });
  }
  
  async initForm() {
    
    if (this.authenticationService.isAuthenticated()) {
      const profiles = await this.profileService.list()
      this.profile = profiles[0];
    } else {
      this.profile = this.profileService.getLocalProfile()!;
    }
    
    this.profileForm.controls["username"].setValue(this.profile.username || null);
    this.profileForm.controls["country"].setValue(this.findCountryByCodeFromDB(this.profile.location.countryCodeISO2)!);
    this.profileForm.controls["location"].setValue(this.profile.location);
  }

  private findCountryByCodeFromDB(alpha2Code: string): Country | undefined {
    return whereAlpha2(alpha2Code);
  }
  
  public async updateProfile(): Promise<void> {
    
    if (this.profileForm.invalid) {
      return;
    }
    
    this.applyForm();
    
    if (this.authenticationService.isAuthenticated()) {
      this.profile = await this.profileService.update(this.profile! as PersistedProfile, (this.profileForm.controls["location"].value as LocationSuggestion)!.reference);
    } else {
      const newLocation = await this.locationsService.create((this.profileForm.controls["location"].value as LocationSuggestion)!.reference);
      this.profile!.location = newLocation;
      this.profileService.persistLocally(this.profile!);  
    }
    
    this.alertService.showSuccess($localize `Profile updated`);
  }
  
  private applyForm() {
    this.profile!.username = this.profileForm.controls["username"].value!;
  }
  
  private resetLocationOptions() {
    this.profileForm.controls["location"].reset();
  }
  
  get selectedCountryCode() {
    const country = this.profileForm.controls["country"].value;
    
    if (!country) {
      return null;
    }
    
    return country.alpha3;
  }
  
  get selectedLocation() {
    return JSON.stringify(this.profileForm.controls["location"].value);
  }
  
  get isValid(): boolean {
    return !this.profileForm.invalid;
  }
}
